import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Plus, Edit, Trash2, Users } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";

const EMPTY = { name: "", cif: "", email: "", phone: "", address: "", notes: "" };

export default function Clients() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/clients").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name) { toast.error("El nombre es obligatorio"); return; }
    try {
      if (editId) await api.put(`/clients/${editId}`, form);
      else await api.post("/clients", form);
      toast.success(editId ? "Cliente actualizado" : "Cliente creado");
      setOpen(false); setForm(EMPTY); setEditId(null); load();
    } catch (e) { toast.error("Error al guardar"); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar cliente?")) return;
    await api.delete(`/clients/${id}`);
    toast.success("Cliente eliminado");
    load();
  };

  const edit = (c) => { setForm(c); setEditId(c.id); setOpen(true); };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="clients-page">
      <header className="flex justify-between items-end flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-cyan mb-2">CRM</div>
          <h1 className="font-display font-bold text-4xl tracking-tight">Clientes</h1>
        </div>
        <Button data-testid="new-client-btn" onClick={() => { setForm(EMPTY); setEditId(null); setOpen(true); }} className="bg-brand-orange hover:bg-brand-orange-hover">
          <Plus className="w-4 h-4 mr-2" /> Nuevo cliente
        </Button>
      </header>

      <div className="rounded-lg border border-white/10 bg-[#0A1128]/40 overflow-hidden">
        {items.length === 0 ? (
          <div className="p-16 text-center" data-testid="clients-empty">
            <Users className="w-12 h-12 mx-auto text-white/20 mb-3" strokeWidth={1} />
            <div className="text-white/60">Aún no tienes clientes guardados.</div>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-white/10 text-[10px] uppercase tracking-widest text-white/50">
                <th className="p-4 font-bold">Nombre</th>
                <th className="p-4 font-bold">CIF/NIF</th>
                <th className="p-4 font-bold">Email</th>
                <th className="p-4 font-bold">Teléfono</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {items.map(c => (
                <tr key={c.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                  <td className="p-4 font-medium">{c.name}</td>
                  <td className="p-4 text-white/60">{c.cif || "—"}</td>
                  <td className="p-4 text-white/60">{c.email || "—"}</td>
                  <td className="p-4 text-white/60">{c.phone || "—"}</td>
                  <td className="p-4 text-right">
                    <Button size="sm" variant="ghost" data-testid={`edit-client-${c.id}`} onClick={() => edit(c)}><Edit className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="ghost" data-testid={`delete-client-${c.id}`} onClick={() => remove(c.id)} className="text-red-400"><Trash2 className="w-3.5 h-3.5" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0A1128] border-white/10 text-white">
          <DialogHeader><DialogTitle className="font-display">{editId ? "Editar cliente" : "Nuevo cliente"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {[
              ["name", "Nombre / Razón social *"],
              ["cif", "CIF / NIF"],
              ["email", "Email"],
              ["phone", "Teléfono"],
              ["address", "Dirección"],
            ].map(([k, label]) => (
              <div key={k}>
                <Label className="text-xs uppercase tracking-widest font-bold text-white/70">{label}</Label>
                <Input data-testid={`client-${k}`} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1.5 bg-black/50 border-white/10 text-white" />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-white/20 bg-transparent">Cancelar</Button>
            <Button data-testid="save-client-btn" onClick={save} className="bg-brand-orange hover:bg-brand-orange-hover">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
